import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';

/**
 * @typedef {object} ReviewData
 * @property {string} id
 * @property {string} customerName
 * @property {number} rating
 * @property {string} [title]
 * @property {string} content
 * @property {string[]} images
 * @property {boolean} verified
 * @property {boolean} featured
 * @property {string} createdAt
 */

/**
 * @typedef {object} SelectedImage
 * @property {File} file
 * @property {string} preview
 */

/**
 * Reviews Drawer Component
 * Displays product reviews in a drawer with pagination and review submission form
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The main dialog element
 * @property {HTMLButtonElement} tabList - Tab button for reviews list
 * @property {HTMLButtonElement} tabForm - Tab button for review form
 * @property {HTMLDivElement} panelList - Panel containing reviews list
 * @property {HTMLDivElement} panelForm - Panel containing review form
 * @property {HTMLDivElement} stats - Stats container
 * @property {HTMLSpanElement} statsAverage - Average rating display
 * @property {HTMLSpanElement} statsStars - Stars display
 * @property {HTMLSpanElement} statsCount - Review count display
 * @property {HTMLDivElement} reviewsList - Reviews list container
 * @property {HTMLDivElement} listLoading - Loading indicator
 * @property {HTMLDivElement} listEmpty - Empty state
 * @property {HTMLButtonElement} loadMoreBtn - Load more button
 * @property {HTMLFormElement} reviewForm - Review submission form
 * @property {HTMLDivElement} ratingInput - Rating stars container
 * @property {HTMLInputElement} ratingValue - Hidden rating input
 * @property {HTMLInputElement} nameInput - Customer name input
 * @property {HTMLInputElement} emailInput - Customer email input
 * @property {HTMLInputElement} titleInput - Review title input
 * @property {HTMLTextAreaElement} contentInput - Review content textarea
 * @property {HTMLInputElement} imageInput - Image file input
 * @property {HTMLDivElement} imagesPreview - Images preview container
 * @property {HTMLDivElement} errorMessage - Error message container
 * @property {HTMLSpanElement} errorText - Error text
 * @property {HTMLDivElement} successMessage - Success message container
 * @property {HTMLSpanElement} successText - Success text
 * @property {HTMLButtonElement} submitButton - Submit button
 * @property {HTMLSpanElement} submitText - Submit button text
 * @property {HTMLSpanElement} submitSpinner - Submit button spinner
 *
 * @extends Component<Refs>
 */
class ReviewsDrawerComponent extends Component {
  requiredRefs = ['dialog'];

  #scrollPosition = 0;
  #productId = '';
  #productHandle = '';
  /** @type {ReviewData[]} */
  #reviews = [];
  #page = 1;
  #hasMore = false;
  #loading = false;
  #selectedRating = 0;
  /** @type {SelectedImage[]} */
  #selectedImages = [];
  #apiEndpoint = '/apps/alux-latest/api/reviews';

  connectedCallback() {
    super.connectedCallback();
    this.#productId = this.dataset.productId || '';
    this.#productHandle = this.dataset.productHandle || '';
    this.#setupRatingInput();
  }

  /**
   * Set product context and reset state
   * @param {string} productId
   * @param {string} productHandle
   */
  setProduct(productId, productHandle) {
    // Only reset if product changed
    if (this.#productId !== productId) {
      this.#productId = productId;
      this.#productHandle = productHandle;
      this.#reviews = [];
      this.#page = 1;
      this.#hasMore = false;
      this.#loading = false;
    }
  }

  /**
   * Setup rating star buttons
   */
  #setupRatingInput() {
    const { ratingInput } = this.refs;
    if (!ratingInput) return;

    const starBtns = ratingInput.querySelectorAll('.reviews-drawer__star-btn');
    starBtns.forEach((btn, index) => {
      btn.addEventListener('click', () => this.#setRating(index + 1));
      btn.addEventListener('mouseenter', () => this.#highlightStars(index + 1));
      btn.addEventListener('mouseleave', () => this.#highlightStars(this.#selectedRating));
    });
  }

  /**
   * Set rating value
   * @param {number} rating
   */
  #setRating(rating) {
    this.#selectedRating = rating;
    const { ratingValue } = this.refs;
    if (ratingValue) ratingValue.value = String(rating);
    this.#highlightStars(rating);
  }

  /**
   * Highlight stars up to the given rating
   * @param {number} rating
   */
  #highlightStars(rating) {
    const { ratingInput } = this.refs;
    if (!ratingInput) return;

    const starBtns = ratingInput.querySelectorAll('.reviews-drawer__star-btn');
    starBtns.forEach((btn, index) => {
      btn.classList.toggle('active', index < rating);
    });
  }

  /**
   * Opens the reviews drawer
   */
  showDialog() {
    const { dialog } = this.refs;
    if (dialog.open) return;

    this.#scrollPosition = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this.#scrollPosition}px`;
    document.body.style.width = '100%';

    dialog.showModal();

    // Fetch reviews on first open
    if (this.#reviews.length === 0) {
      this.#fetchReviews();
    }
  }

  /**
   * Closes the reviews drawer
   */
  async closeDialog() {
    const { dialog } = this.refs;
    if (!dialog.open) return;

    dialog.style.animation = 'none';
    void dialog.offsetWidth;
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    await onAnimationEnd(dialog, undefined, { subtree: false });
    dialog.close();
    dialog.classList.remove('dialog-closing');

    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    window.scrollTo(0, this.#scrollPosition);
  }

  /**
   * Switch between tabs
   * @param {Event} event
   */
  switchTab(event) {
    const target = /** @type {HTMLElement} */ (event.target);
    const tabName = target.dataset.tab;
    if (!tabName) return;

    const { tabList, tabForm, panelList, panelForm } = this.refs;

    // Update tab states
    tabList?.classList.toggle('reviews-drawer__tab--active', tabName === 'list');
    tabForm?.classList.toggle('reviews-drawer__tab--active', tabName === 'form');

    // Update panel visibility
    panelList?.classList.toggle('reviews-drawer__panel--active', tabName === 'list');
    panelForm?.classList.toggle('reviews-drawer__panel--active', tabName === 'form');

    if (panelList) panelList.hidden = tabName !== 'list';
    if (panelForm) panelForm.hidden = tabName !== 'form';
  }

  /**
   * Show the form tab
   */
  showFormTab() {
    const { tabForm } = this.refs;
    if (tabForm) {
      tabForm.click();
    }
  }

  /**
   * Fetch reviews from API
   */
  async #fetchReviews() {
    if (this.#loading) return;
    this.#loading = true;

    const { listLoading, listEmpty, loadMoreBtn } = this.refs;
    if (listLoading) listLoading.hidden = false;
    if (listEmpty) listEmpty.hidden = true;

    try {
      const url = `${this.#apiEndpoint}?product_id=${this.#productId}&page=${this.#page}&limit=10`;
      const response = await fetch(url);
      const data = await response.json();

      if (data.error) {
        throw new Error(data.error);
      }

      // Update stats
      this.#updateStats(data.stats);

      // Append reviews
      if (data.reviews && data.reviews.length > 0) {
        this.#reviews = [...this.#reviews, ...data.reviews];
        this.#renderReviews();
      }

      // Update pagination
      this.#hasMore = data.pagination?.hasMore || false;
      if (loadMoreBtn) loadMoreBtn.hidden = !this.#hasMore;

      // Show empty state if no reviews
      if (this.#reviews.length === 0 && listEmpty) {
        listEmpty.hidden = false;
      }

    } catch (e) {
      console.error('Failed to fetch reviews:', e);
    } finally {
      this.#loading = false;
      if (listLoading) listLoading.hidden = true;
    }
  }

  /**
   * Update stats display
   * @param {{ averageRating: number, totalReviews: number }} stats
   */
  #updateStats(stats) {
    const { statsAverage, statsStars, statsCount } = this.refs;

    if (statsAverage) {
      statsAverage.textContent = stats.averageRating ? stats.averageRating.toFixed(1) : '--';
    }

    if (statsStars) {
      const fullStars = Math.floor(stats.averageRating || 0);
      const halfStar = (stats.averageRating || 0) - fullStars >= 0.5;
      statsStars.textContent = '★'.repeat(fullStars) + (halfStar ? '½' : '') + '☆'.repeat(5 - fullStars - (halfStar ? 1 : 0));
    }

    if (statsCount) {
      statsCount.textContent = String(stats.totalReviews || 0);
    }
  }

  /**
   * Render reviews list
   */
  #renderReviews() {
    const { reviewsList, listLoading } = this.refs;
    if (!reviewsList) return;

    // Clear existing reviews (keep loading indicator)
    const existingReviews = reviewsList.querySelectorAll('.reviews-drawer__review');
    existingReviews.forEach(el => el.remove());

    // Render each review
    this.#reviews.forEach(review => {
      const el = this.#createReviewElement(review);
      if (listLoading) {
        reviewsList.insertBefore(el, listLoading);
      } else {
        reviewsList.appendChild(el);
      }
    });
  }

  /**
   * Create a review element
   * @param {ReviewData} review
   * @returns {HTMLElement}
   */
  #createReviewElement(review) {
    const article = document.createElement('article');
    article.className = 'reviews-drawer__review';

    const date = new Date(review.createdAt).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });

    const stars = '★'.repeat(review.rating) + '☆'.repeat(5 - review.rating);

    let badges = '';
    if (review.verified) {
      badges += '<span class="reviews-drawer__badge reviews-drawer__badge--verified">✓ Achat vérifié</span>';
    }
    if (review.featured) {
      badges += '<span class="reviews-drawer__badge reviews-drawer__badge--featured">★ En vedette</span>';
    }

    let images = '';
    if (review.images && review.images.length > 0) {
      images = `
        <div class="reviews-drawer__review-images">
          ${review.images.map(img => `<img src="${img}" alt="Photo avis" class="reviews-drawer__review-image" loading="lazy">`).join('')}
        </div>
      `;
    }

    article.innerHTML = `
      <div class="reviews-drawer__review-header">
        <div class="reviews-drawer__review-author">
          <span class="reviews-drawer__review-name">${this.#escapeHtml(review.customerName)}</span>
          ${badges ? `<div class="reviews-drawer__review-badges">${badges}</div>` : ''}
        </div>
        <span class="reviews-drawer__review-date">${date}</span>
      </div>
      <div class="reviews-drawer__review-stars">${stars}</div>
      ${review.title ? `<h4 class="reviews-drawer__review-title">${this.#escapeHtml(review.title)}</h4>` : ''}
      <p class="reviews-drawer__review-content">${this.#escapeHtml(review.content)}</p>
      ${images}
    `;

    return article;
  }

  /**
   * Escape HTML to prevent XSS
   * @param {string} str
   * @returns {string}
   */
  #escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  /**
   * Load more reviews
   */
  loadMore() {
    if (this.#loading || !this.#hasMore) return;
    this.#page++;
    this.#fetchReviews();
  }

  /**
   * Handle image selection
   * @param {Event} event
   */
  handleImageSelect(event) {
    const input = /** @type {HTMLInputElement} */ (event.target);
    const files = Array.from(input.files || []);

    // Limit to 3 images total
    const remaining = 3 - this.#selectedImages.length;
    const newFiles = files.slice(0, remaining);

    newFiles.forEach(file => {
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          this.#selectedImages.push({
            file,
            preview: /** @type {string} */ (e.target?.result)
          });
          this.#renderImagePreviews();
        };
        reader.readAsDataURL(file);
      }
    });

    // Clear input for re-selection
    input.value = '';
  }

  /**
   * Render image previews
   */
  #renderImagePreviews() {
    const { imagesPreview } = this.refs;
    if (!imagesPreview) return;

    imagesPreview.innerHTML = this.#selectedImages.map((img, index) => `
      <div class="reviews-drawer__image-preview" data-index="${index}">
        <img src="${img.preview}" alt="Aperçu">
        <button type="button" class="reviews-drawer__image-remove" data-index="${index}">×</button>
      </div>
    `).join('');

    // Add remove handlers
    imagesPreview.querySelectorAll('.reviews-drawer__image-remove').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const target = /** @type {HTMLElement} */ (e.target);
        const index = parseInt(target.dataset.index || '0', 10);
        this.#selectedImages.splice(index, 1);
        this.#renderImagePreviews();
      });
    });
  }

  /**
   * Submit review form
   * @param {Event} event
   */
  async submitReview(event) {
    event.preventDefault();
    this.#hideError();
    this.#hideSuccess();

    const { nameInput, emailInput, titleInput, contentInput, ratingValue } = this.refs;

    // Validation
    if (!ratingValue?.value || parseInt(ratingValue.value, 10) < 1) {
      this.#showError('Veuillez sélectionner une note');
      return;
    }

    if (!nameInput?.value?.trim()) {
      this.#showError('Veuillez entrer votre nom');
      return;
    }

    if (!contentInput?.value?.trim()) {
      this.#showError('Veuillez écrire votre avis');
      return;
    }

    // Show loading
    this.#showLoading();

    try {
      // Upload images first if any (using Shopify staged uploads would require backend setup)
      // For now, we'll skip actual image upload and just store the file info
      /** @type {string[]} */
      const imageUrls = [];
      // TODO: Implement actual image upload via Shopify Files API from backend

      const body = {
        productId: this.#productId,
        productHandle: this.#productHandle,
        customerName: nameInput.value.trim(),
        customerEmail: emailInput?.value?.trim() || null,
        rating: parseInt(ratingValue.value, 10),
        title: titleInput?.value?.trim() || null,
        content: contentInput.value.trim(),
        images: imageUrls
      };

      const response = await fetch(this.#apiEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      const data = await response.json();

      if (!response.ok || data.error) {
        throw new Error(data.error || 'Une erreur est survenue');
      }

      // Success
      this.#showSuccess(data.message || 'Merci pour votre avis !');
      this.#resetForm();

      // Switch to list tab after a delay
      setTimeout(() => {
        this.#reviews = [];
        this.#page = 1;
        this.#fetchReviews();
        const { tabList } = this.refs;
        if (tabList) tabList.click();
      }, 2000);

    } catch (e) {
      console.error('Failed to submit review:', e);
      this.#showError(e instanceof Error ? e.message : 'Une erreur est survenue');
    } finally {
      this.#hideLoading();
    }
  }

  /**
   * Reset form
   */
  #resetForm() {
    const { reviewForm } = this.refs;
    if (reviewForm) reviewForm.reset();
    this.#selectedRating = 0;
    this.#highlightStars(0);
    this.#selectedImages = [];
    this.#renderImagePreviews();
  }

  /**
   * Show loading state
   */
  #showLoading() {
    const { submitButton, submitText, submitSpinner } = this.refs;
    if (submitButton) submitButton.classList.add('is-loading');
    if (submitText) submitText.hidden = true;
    if (submitSpinner) submitSpinner.hidden = false;
  }

  /**
   * Hide loading state
   */
  #hideLoading() {
    const { submitButton, submitText, submitSpinner } = this.refs;
    if (submitButton) submitButton.classList.remove('is-loading');
    if (submitText) submitText.hidden = false;
    if (submitSpinner) submitSpinner.hidden = true;
  }

  /**
   * Show error message
   * @param {string} message
   */
  #showError(message) {
    const { errorMessage, errorText } = this.refs;
    if (errorMessage && errorText) {
      errorText.textContent = message;
      errorMessage.hidden = false;
    }
  }

  /**
   * Hide error message
   */
  #hideError() {
    const { errorMessage } = this.refs;
    if (errorMessage) errorMessage.hidden = true;
  }

  /**
   * Show success message
   * @param {string} message
   */
  #showSuccess(message) {
    const { successMessage, successText } = this.refs;
    if (successMessage && successText) {
      successText.textContent = message;
      successMessage.hidden = false;
    }
  }

  /**
   * Hide success message
   */
  #hideSuccess() {
    const { successMessage } = this.refs;
    if (successMessage) successMessage.hidden = true;
  }
}

if (!customElements.get('reviews-drawer-component')) {
  customElements.define('reviews-drawer-component', ReviewsDrawerComponent);
}

export { ReviewsDrawerComponent };
